// src/hooks/useClientesConRFM.js
import { useState, useEffect } from 'react';
import { get } from '../helpers/apiHelper';
import useAuthStore from '../store/authStore';

/**
 * Hook personalizado para obtener clientes con datos RFM combinados
 * 
 * Combina datos de:
 * - /api/clientes/byUser (datos básicos)
 * - /api/clientes/analytics/rfm?period=12 (datos RFM)
 */
export const useClientesConRFM = (period = 12) => {
    const { user } = useAuthStore();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchClientesConRFM = async () => {
            if (!user) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // 1. Obtener clientes básicos
                const clientesData = await get('clientes/byUser');

                // 2. Obtener datos RFM
                const rfmResponse = await get(`clientes/analytics/rfm?period=${period}`);

                // 3. Crear mapa de RFM por clienteId
                const rfmMap = new Map();
                
                if (rfmResponse.rfmData && Array.isArray(rfmResponse.rfmData)) {
                    rfmResponse.rfmData.forEach(item => {
                        // El clienteId puede estar en diferentes formatos
                        const clienteId = item.clienteId?.toString() || 
                                        item.cliente?._id?.toString() || 
                                        item.clienteId;
                        
                        if (!clienteId) {
                            return;
                        }

                        // Convertir rfmScore de string a número si es necesario
                        let rfmScore = item.rfmScore;
                        if (typeof rfmScore === 'string') {
                            // Si es string como "135" o "000", calcular el total sumando dígitos
                            const digits = rfmScore.replace(/\D/g, '');
                            if (digits.length === 3) {
                                // Sumar los tres dígitos para obtener el score total (1-15)
                                // Ejemplo: "135" → 1+3+5 = 9, "000" → 0+0+0 = 0
                                rfmScore = parseInt(digits[0]) + parseInt(digits[1]) + parseInt(digits[2]);
                            } else if (digits.length > 0) {
                                rfmScore = parseInt(digits) || 0;
                            } else {
                                rfmScore = 0;
                            }
                        } else if (rfmScore === undefined || rfmScore === null) {
                            rfmScore = 0;
                        }

                        // Manejar avg_basket_size: usar averageOrderValue si existe (incluso si es 0)
                        let avgBasketSize = item.avg_basket_size;
                        if (avgBasketSize === undefined || avgBasketSize === null) {
                            avgBasketSize = item.averageOrderValue;
                        }
                        // Si sigue siendo undefined/null, usar 0 como valor por defecto
                        if (avgBasketSize === undefined || avgBasketSize === null) {
                            avgBasketSize = 0;
                        }

                        rfmMap.set(clienteId, {
                            rfmScore: rfmScore,
                            segment: item.segment,
                            avg_basket_size: avgBasketSize,
                            frequency: item.frequency !== undefined && item.frequency !== null 
                                ? item.frequency 
                                : (item.totalPurchases !== undefined && item.totalPurchases !== null 
                                    ? item.totalPurchases 
                                    : 0),
                            recency: item.recency,
                            monetary: item.monetary,
                            lastPurchase: item.lastPurchase,
                            totalPurchases: item.totalPurchases
                        });
                    });
                }

                // 4. Combinar datos y calcular valores reales desde tickets si el backend devuelve 0
                const clientesConRFM = clientesData.map(cliente => {
                    const clienteId = cliente._id?.toString();
                    const rfmInfo = rfmMap.get(clienteId);
                    
                    // Calcular frequency real desde tickets (contar consumiciones)
                    const frequencyReal = cliente.tickets?.reduce((total, ticket) => {
                        return total + (ticket.historialConsumiciones?.length || 0);
                    }, 0) || 0;
                    
                    // Calcular monto total y avg_basket_size real desde tickets
                    let montoTotalReal = 0;
                    cliente.tickets?.forEach(ticket => {
                        ticket.historialConsumiciones?.forEach(cons => {
                            montoTotalReal += (cons.monto || 0);
                        });
                    });
                    const avgBasketSizeReal = frequencyReal > 0 ? montoTotalReal / frequencyReal : 0;
                    
                    // Usar valores del backend si son correctos (> 0), sino usar valores calculados
                    const frequencyFinal = (rfmInfo?.frequency && rfmInfo.frequency > 0) 
                        ? rfmInfo.frequency 
                        : frequencyReal;
                    
                    const avgBasketSizeFinal = (rfmInfo?.avg_basket_size && rfmInfo.avg_basket_size > 0)
                        ? rfmInfo.avg_basket_size
                        : (rfmInfo?.averageOrderValue && rfmInfo.averageOrderValue > 0)
                            ? rfmInfo.averageOrderValue
                            : avgBasketSizeReal;
                    
                    const clienteCombinado = {
                        ...cliente,
                        // Añadir datos RFM si existen
                        ...(rfmInfo ? {
                            rfmScore: rfmInfo.rfmScore,
                            segment: rfmInfo.segment,
                            avg_basket_size: avgBasketSizeFinal,
                            frequency: frequencyFinal,
                            recency: rfmInfo.recency,
                            monetary: rfmInfo.monetary || montoTotalReal,
                            lastPurchase: rfmInfo.lastPurchase
                        } : {
                            // Si no hay datos RFM del backend, calcular desde tickets
                            rfmScore: undefined,
                            segment: undefined,
                            avg_basket_size: avgBasketSizeReal > 0 ? avgBasketSizeReal : undefined,
                            frequency: frequencyReal > 0 ? frequencyReal : undefined
                        })
                    };

                    return clienteCombinado;
                });

                setClientes(clientesConRFM);
            } catch (err) {
                console.error('Error obteniendo clientes con RFM:', err);
                setError(err);
                
                // En caso de error, intentar al menos devolver los clientes básicos
                try {
                    const clientesData = await get('clientes/byUser');
                    setClientes(clientesData);
                } catch (fallbackError) {
                    console.error('Error en fallback:', fallbackError);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchClientesConRFM();
    }, [user, period]);

    return { clientes, loading, error };
};

export default useClientesConRFM;

